import omit from 'lodash/omit'

import api from 'api'
import { get } from 'utils/lodash'

import { optimisticNoteCreate } from './optimisticNoteCount'

import {
  CACHE_TAG_ACCOUNT_LOCATION_DETAILS,
  CACHE_TAG_ACCOUNT_LOCATION_LIST,
  CACHE_TAG_CONFIGURED_SERVICE_NOTES_LIST,
  CACHE_TAG_CONFIGURED_SERVICES_LIST,
  CACHE_TAG_ROUTES,
  CACHE_TAG_ROUTES_UNASSIGNED_WORK_ORDERS,
  CACHE_TAG_WORK_ORDER_LIST,
} from '../cacheTagTypes'

type NoteRecord = Record<string, unknown>
type NotesResponse = { notes: NoteRecord[] }
// Writes carry note text, mobile-visibility and archive flags depending on the caller;
// skipInvalidate is a client-only flag stripped before the request goes out.
type CSNoteBody = Record<string, unknown> & { skipInvalidate?: boolean }

type CSNoteArg = { id: string; noteId: string; data?: CSNoteBody }

// The route board's note badge counts service notes, so every write has to move it too.
const invalidatesTags = [
  CACHE_TAG_CONFIGURED_SERVICES_LIST,
  CACHE_TAG_WORK_ORDER_LIST,
  CACHE_TAG_ACCOUNT_LOCATION_LIST,
  CACHE_TAG_ACCOUNT_LOCATION_DETAILS,
  CACHE_TAG_ROUTES,
  CACHE_TAG_ROUTES_UNASSIGNED_WORK_ORDERS,
]

const extendedApi = api.injectEndpoints({
  endpoints: builder => ({
    getAllCSNotes: builder.query<NotesResponse, { id: string }>({
      query: ({ id }) => ({
        url: `/api/v1/core/configured-services/${id}/notes`,
        method: 'GET',
      }),
      providesTags: (result, error, { id }) => [
        CACHE_TAG_CONFIGURED_SERVICE_NOTES_LIST,
        { type: CACHE_TAG_CONFIGURED_SERVICE_NOTES_LIST, id },
      ],
    }),
    getCSNoteDetails: builder.query<NoteRecord, { configuredServiceId: string; noteId: string }>({
      query: ({ configuredServiceId, noteId }) => ({
        url: `/api/v1/core/configured-services/${configuredServiceId}/notes/${noteId}`,
        method: 'GET',
      }),
    }),
    getCSNoteHistory: builder.query<NotesResponse, { id: string; noteId: string }>({
      query: ({ id, noteId }) => ({
        url: `/api/v1/core/configured-services/${id}/notes/${noteId}/history`,
        method: 'GET',
      }),
    }),
    createCSNote: builder.mutation<NoteRecord, { id: string; data: CSNoteBody }>({
      query: ({ id, data }) => ({
        url: `/api/v1/core/configured-services/${id}/notes`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: CACHE_TAG_CONFIGURED_SERVICE_NOTES_LIST, id }, ...invalidatesTags],
      // Every stop backed by this service counts the note, so the badges move now and the
      // invalidation above reconciles afterwards.
      onQueryStarted: optimisticNoteCreate('configuredServiceNoteCount', id => stop => get(stop, 'configuredServiceId', '') === id),
    }),
    updateCSNote: builder.mutation<NoteRecord, CSNoteArg>({
      query: ({ id, noteId, data }) => ({
        url: `/api/v1/core/configured-services/${id}/notes/${noteId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: CACHE_TAG_CONFIGURED_SERVICE_NOTES_LIST, id }, ...invalidatesTags],
    }),
    patchCSNote: builder.mutation<NoteRecord, CSNoteArg>({
      query: ({ id, noteId, data }) => ({
        url: `/api/v1/core/configured-services/${id}/notes/${noteId}`,
        method: 'PATCH',
        body: omit(data, 'skipInvalidate'),
      }),
      invalidatesTags: (result, error, arg) =>
        arg?.data?.skipInvalidate ? [] : [{ type: CACHE_TAG_CONFIGURED_SERVICE_NOTES_LIST, id: arg.id }, ...invalidatesTags],
    }),
    deleteCSNote: builder.mutation<NoteRecord, CSNoteArg>({
      query: ({ id, noteId, data }) => ({
        url: `/api/v1/core/configured-services/${id}/notes/${noteId}/delete`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: CACHE_TAG_CONFIGURED_SERVICE_NOTES_LIST, id }, ...invalidatesTags],
    }),
  }),
})

export const {
  useLazyGetAllCSNotesQuery,
  useLazyGetCSNoteDetailsQuery,
  useLazyGetCSNoteHistoryQuery,
  useCreateCSNoteMutation,
  useUpdateCSNoteMutation,
  usePatchCSNoteMutation,
  useDeleteCSNoteMutation,
} = extendedApi
